import { reactive, watch } from "vue";

export function useModuleUI({
    accessibilityContentRef,
    customSectionRef,
    sectionContentRef,
    zoom,
    isDarkModeRef,
    applyCursorThemeDefaults,
    darkModeStorageKey,
    zoomWrapper = "#zoomWrapper",
    refreshSnapshot,
}) {
    const state = reactive({
        isModuleActive: false,
        isShowCustom: false,
        hasCustomCta: false,
        isZoomExpanded: true,
        moduleWidthOnFontActive: "420px",
        isVisualZoomMenuOpen: false,
        visualProfileZoomValue: 100,
        visualProfileZoomStep: 5,
    });

    function applyDarkMode() {
        document.documentElement.style.colorScheme = isDarkModeRef.value ? "dark" : "light";
    }

    function manageWidthOnToggleFont(isFontActive) {
        if (!accessibilityContentRef.value) return;
        if (isFontActive && state.isModuleActive) {
            accessibilityContentRef.value.style.width = state.moduleWidthOnFontActive;
        } else {
            accessibilityContentRef.value.style.removeProperty("width");
        }
    }

    function resizeModule() {
        if (!customSectionRef.value) return;
        if (state.isShowCustom) {
            customSectionRef.value.style.maxHeight = customSectionRef.value.scrollHeight + "px";
        } else {
            customSectionRef.value.style.maxHeight = 0;
        }
    }

    function setModuleContentHeight() {
        if (accessibilityContentRef.value) accessibilityContentRef.value.style.maxHeight = "90vh";
    }

    function toggleModule(magnifierViewRef) {
        if (!state.hasCustomCta) {
            state.hasCustomCta = !!document.querySelector("[data-tenjity-customcta='true']");
        }
        state.isModuleActive = !state.isModuleActive;

        const isFontActive = sectionContentRef.value?.$data?.isFontActive;
        if (!state.isModuleActive && isFontActive) {
            accessibilityContentRef.value?.style.removeProperty("width");
        }
        if (state.isModuleActive && isFontActive) {
            if (accessibilityContentRef.value) accessibilityContentRef.value.style.width = state.moduleWidthOnFontActive;
        }

        resizeModule();

        if (magnifierViewRef?.value) {
            const content = accessibilityContentRef.value;
            const onTransitionEnd = () => {
                content.removeEventListener("transitionend", onTransitionEnd);
                magnifierViewRef.value?.captureSnapshot();
            };
            content.addEventListener("transitionend", onTransitionEnd);
        }
    }

    function toggleShowCustom() {
        state.isShowCustom = !state.isShowCustom;
        refreshSnapshot(120);
    }

    function toggleVisualZoomMenu() {
        state.isVisualZoomMenuOpen = !state.isVisualZoomMenuOpen;
        refreshSnapshot(380);
    }

    function applyVisualZoom() {
        const appContainer = document.querySelector(zoomWrapper);
        if (!appContainer) return;
        const zoomValue = Number(state.visualProfileZoomValue) || 100;
        if (typeof document.documentElement.style.zoom === "undefined") {
            const scale = zoomValue / 100;
            appContainer.style.transform = `scale(${scale})`;
            appContainer.style.transformOrigin = "top left";
            appContainer.style.width = `${100 / scale}%`;
            appContainer.style.height = `${100 / scale}%`;
            document.body.style.overflow = "auto";
        } else {
            appContainer.style.zoom = `${zoomValue}%`;
            appContainer.style.removeProperty("transform");
            appContainer.style.removeProperty("transform-origin");
            appContainer.style.removeProperty("width");
            appContainer.style.removeProperty("height");
        }
        refreshSnapshot(120);
    }

    function resetZoomGlobal() {
        zoom.resetPageZoom();
        zoom.resetModuleZoom();
    }

    function resetContentScroll() {
        if (!accessibilityContentRef.value) return;
        accessibilityContentRef.value.scrollTop = 0;
        setModuleContentHeight();
    }

    function changeVisualProfileZoom(type) {
        if (type === "minus" && state.visualProfileZoomValue > state.visualProfileZoomStep) {
            state.visualProfileZoomValue -= state.visualProfileZoomStep;
        }
        if (type === "more") {
            state.visualProfileZoomValue += state.visualProfileZoomStep;
        }
        applyVisualZoom();
        refreshSnapshot(120);
    }

    function updateVisualProfileZoomValue(value) {
        state.visualProfileZoomValue = value;
        applyVisualZoom();
        refreshSnapshot(120);
    }

    watch(isDarkModeRef, (val) => {
        applyDarkMode();
        applyCursorThemeDefaults();
        window.localStorage.setItem(darkModeStorageKey, JSON.stringify(val));
        refreshSnapshot(120);
    });

    watch(() => state.isModuleActive, (active) => {
        if (active) {
            setModuleContentHeight();
            if (zoom.state.isZoomActive) zoom.applyZoom();
            if (zoom.state.isModuleZoomActive) zoom.applyModuleZoom();
            if (accessibilityContentRef.value) accessibilityContentRef.value.scrollTop = 0;
        } else {
            if (accessibilityContentRef.value) accessibilityContentRef.value.style.maxHeight = 0;
        }
    });

    watch(() => state.isShowCustom, () => resizeModule());

    watch(() => state.hasCustomCta, (val) => {
        if (val && !state.isModuleActive) {
            const header = document.querySelector(".tenjityModule-header");
            if (header) header.style.display = "none";
        }
    });

    return {
        state,
        applyDarkMode,
        manageWidthOnToggleFont,
        resizeModule,
        toggleModule,
        toggleShowCustom,
        toggleVisualZoomMenu,
        applyVisualZoom,
        resetZoomGlobal,
        resetContentScroll,
        changeVisualProfileZoom,
        updateVisualProfileZoomValue,
    };
}
